import Flex from "@/components/atoms/Flex";
import PhoneCallLink from "@/components/atoms/PhoneCallLink";
import PopCalendar from "@/components/atoms/PopCalendar";
import SoftBadge from "@/components/atoms/SoftBadge";
import Company from "@/interfaces/Company";
import CompanyCourse from "@/interfaces/CompanyCourse";
import { IFilters, IMeta } from "@/interfaces/Fetch";
import {
  faBuilding,
  faClock,
  faEllipsisVertical,
  faEuro,
  faFolderOpen,
  faGraduationCap,
  faPeopleGroup,
  faPerson,
  faUserTie,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DateTime } from "luxon";
import { Dispatch, ElementRef, FC, SetStateAction, useRef, useState } from "react";
import { Col, Dropdown, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import AreYouSurToContinueModal from "../../modals/AreYouSurToContinueModal";
import PaginateCard from "../PaginateCard";
import classNames from "classnames";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "@/services/slices/authSlice";
import { resolveCourseContact } from "@/helpers/CompanyHelpers";
import { checkIfUserHasRoles } from "@/helpers/UserHelpers";
import PreformatifDiagnosticModal from "../../modals/company/courses/PreformatifDiagnosticModal";

export interface CourseCardProps {
  data?: CompanyCourse[];
  /** Porte le gérant et les téléphones, que l'endpoint des formations ne
   *  renvoie pas : la fiche entreprise l'a déjà chargée et la fait descendre. */
  company?: Company;
  meta?: IMeta;
  filters: IFilters;
  onDelete?: (id: string) => Promise<void>;
  showDelete?: boolean;
  setFilters: Dispatch<SetStateAction<IFilters>>;
}

const CoursesCard: FC<CourseCardProps> = ({
  data = [],
  company,
  meta,
  onDelete,
  filters,
  showDelete = true,
  setFilters,
}) => {
  const user = useSelector(selectCurrentUser);
  const DeleteModalRef =
    useRef<ElementRef<typeof AreYouSurToContinueModal>>(null);
  const [diagnosticCourseId, setDiagnosticCourseId] = useState<string>();

  const onContinue = async (id: string) => {
    try {
      await onDelete?.(id);
    } finally {
      DeleteModalRef.current?.showOrHiddenModal();
    }
  };

  return (
    <PaginateCard filters={filters} setFilters={setFilters} meta={meta}>
      {diagnosticCourseId && data[0]?.companyId && <PreformatifDiagnosticModal companyId={data[0].companyId} courseId={diagnosticCourseId} onHide={() => setDiagnosticCourseId(undefined)} />}
      {showDelete && (
        <AreYouSurToContinueModal
          ref={DeleteModalRef}
          title="Êtes-vous sûr de vouloir supprimer cette formation ?"
          onContinue={onContinue}
        />
      )}
      {data.map(
        ({
          id,
          startAt,
          hoursNumber,
          course,
          instructorId,
          status,
          companyId,
          instructor,
          people,
          trainingType,
          organisation,
          finalFolderNumber,
        }) => {
          const contact = resolveCourseContact(company);
          // Le gérant a déjà sa ligne quand c'est lui le contact : son numéro
          // rejoint alors les autres au lieu de dupliquer son nom.
          const ownerOnItsOwnLine =
            contact?.label === "Interlocuteur" && !!company?.owner?.phone;
          const phones = [
            company?.fixPhone,
            company?.phone,
            ownerOnItsOwnLine ? null : company?.owner?.phone,
          ].filter((phone): phone is string => !!phone);

          return (
            <div
              key={id}
              className="bg-white dark__bg-1100 p-x1 rounded-3 shadow-sm"
            >
              <Row className="flex-between-center">
                <Col xs={10}>
                  <Link
                    to={`/companies/${companyId}/courses/${id}`}
                    className="d-flex align-items-center gap-1 h6 flex-1"
                  >
                    {startAt && <PopCalendar date={startAt} />}
                    <p
                      className={classNames("fw-semi-bold mb-0", {
                        "ms-2": startAt,
                      })}
                    >
                      {course?.name} {hoursNumber && `(${hoursNumber}h)`}
                    </p>
                  </Link>
                </Col>
                {showDelete &&
                  checkIfUserHasRoles(user, ["ADMIN", "AE", "CSM"]) && (
                    <Col xs="auto" className="d-flex justify-content-end">
                      <Dropdown
                        align="end"
                        className="btn-reveal-trigger d-inline-block"
                      >
                        <Dropdown.Toggle
                          split
                          variant="nolina-default"
                          size="sm"
                        >
                          <FontAwesomeIcon
                            icon={faEllipsisVertical}
                            className="fs--2"
                          />
                        </Dropdown.Toggle>

                        <Dropdown.Menu className="border py-0">
                          <div className="py-2">
                            <Dropdown.Item
                              as={Link}
                              to={`/companies/${companyId}/courses/${id}/edit`}
                            >
                              Modifier
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => setDiagnosticCourseId(id)}>
                              Diagnostic préformatif
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item
                              className="text-danger"
                              onClick={() =>
                                DeleteModalRef.current?.showOrHiddenModal(
                                  id.toString(),
                                )
                              }
                            >
                              Supprimer
                            </Dropdown.Item>
                          </div>
                        </Dropdown.Menu>
                      </Dropdown>
                    </Col>
                  )}
              </Row>
              <div className="border-bottom border-0 my-3"></div>
              <Row className="flex-between-center">
                <Col xs={12}>
                  <h6 className="text-600 mb-3 mb-sm-2 lb-anywhere">
                    {course?.description}
                  </h6>
                  {instructor && (
                    <Link
                      to={`/instructors/${instructorId}`}
                      className="d-flex align-items-center gap-1 h6 mb-1"
                    >
                      <FontAwesomeIcon
                        icon={faGraduationCap}
                        transform="shrink-3 up-1"
                      />
                      <span className="text-600">{instructor.fullname}</span>
                    </Link>
                  )}
                  {organisation && (
                    <div className="mb-2">
                      <SoftBadge
                        pill
                        bg={organisation.backgroundColor || "secondary"}
                        textColor={organisation.textColor}
                        className="d-inline-flex align-items-center px-2 py-1"
                        textClassName="d-inline-flex align-items-center gap-1"
                      >
                        <FontAwesomeIcon icon={faBuilding} className="fs--2" />
                        <span title="Organisme de formation">
                          {organisation.name}
                        </span>
                      </SoftBadge>
                    </div>
                  )}
                  {finalFolderNumber && (
                    <h6 className="d-flex align-items-center gap-2 mb-1">
                      <FontAwesomeIcon
                        icon={faFolderOpen}
                        transform="shrink-3 up-1"
                      />
                      <span className="text-600">
                        N° de dossier final : {finalFolderNumber}
                      </span>
                    </h6>
                  )}
                  {hoursNumber && (
                    <h6 className="d-flex align-items-center gap-2 mb-1">
                      <FontAwesomeIcon
                        icon={faClock}
                        transform="shrink-3 up-1"
                      />
                      <span className="text-600">{hoursNumber} heure(s)</span>
                    </h6>
                  )}
                  {trainingType && (
                    <h6 className="d-flex align-items-center gap-2 mb-1">
                      <FontAwesomeIcon
                        icon={faPerson}
                        transform="shrink-3 up-1"
                      />
                      <span className="text-600">
                        {trainingType === 'presentiel' ? 'Présentiel' : 'Distanciel'}
                      </span>
                    </h6>
                  )}
                  {contact && (
                    <h6 className="d-flex align-items-center gap-2 mb-1">
                      <FontAwesomeIcon
                        icon={faPerson}
                        transform="shrink-3 up-1"
                      />
                      <span className="text-600">
                        {contact.label} : {contact.name}
                      </span>
                    </h6>
                  )}
                  {phones.length > 0 && (
                    <div className="d-flex flex-wrap align-items-center gap-3 mb-1">
                      {phones.map((phone) => (
                        <PhoneCallLink key={phone} number={phone} />
                      ))}
                    </div>
                  )}
                  {ownerOnItsOwnLine && company?.owner && (
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                      <h6 className="d-flex align-items-center gap-2 mb-0">
                        <FontAwesomeIcon
                          icon={faUserTie}
                          transform="shrink-3 up-1"
                        />
                        <span className="text-600">
                          Gérant {company.owner.fullname}
                        </span>
                      </h6>
                      <PhoneCallLink number={company.owner.phone!} />
                    </div>
                  )}
                  {people.length > 0 && (
                    <Flex alignItems="center">
                      <FontAwesomeIcon
                        icon={faPeopleGroup}
                        transform="shrink-3 up-1"
                      />
                      {people.map((person) => (
                        <Link to={`/people/${person.id}`}>
                          <span className="badge border link-secondary text-decoration-none mx-1 my-2">
                            {person.fullname}
                          </span>
                        </Link>
                      ))}
                    </Flex>
                  )}
                </Col>
                <Col xs={12}>
                  <div className="border-bottom border-0 my-3"></div>
                  <Flex direction="column">
                    {startAt && (
                      <h6 className="d-flex align-items-center gap-1 mb-2">
                        <FontAwesomeIcon
                          icon={faClock}
                          transform="shrink-3 up-1"
                        />
                        <span className="text-600">
                          {DateTime.fromISO(startAt)
                            .setLocale("fr")
                            .toFormat("dd LLL yyyy")}
                        </span>
                      </h6>
                    )}
                    <SoftBadge
                      bg={status?.backgroundColor ?? "danger"}
                      textColor={status?.textColor}
                    >
                      {status?.name ?? "Inconnu"}
                    </SoftBadge>
                  </Flex>
                </Col>
              </Row>
            </div>
          );
        }
      )}
    </PaginateCard>
  );
};

export default CoursesCard;
